import * as THREE from "three";
import { psxMaterial, psxEmissive, type PsxUniforms } from "./psx/PsxMaterial.js";
import { textures } from "./textures/procedural.js";
import { hangPlate, type RoomAnchors, type RoomMotion } from "./ReactorRoom.js";
import { Fixtures } from "./fixtures.js";

/**
 * The boarding gangway: a narrow corridor ending in a sealed hatch, with a card-key kiosk on the
 * bulkhead at eye height. This is the crew access terminal — the first thing a player sees.
 */
export function buildGangway(shared: PsxUniforms): { group: THREE.Group; anchors: RoomAnchors; fixtures: Fixtures; motion: RoomMotion; dispose(): void } {
  const g = new THREE.Group();
  const mats: THREE.Material[] = [];
  const geos: THREE.BufferGeometry[] = [];
  const mat = (t: THREE.Texture, uv: [number, number] = [1, 1], tint?: THREE.ColorRepresentation) => { const m = psxMaterial({ map: t, uvScale: uv, tint }, shared); mats.push(m); return m; };
  const box = (w: number, h: number, d: number, m: THREE.Material, x: number, y: number, z: number) => {
    const geo = new THREE.BoxGeometry(w, h, d); geos.push(geo);
    const mesh = new THREE.Mesh(geo, m); mesh.position.set(x, y, z); g.add(mesh); return mesh;
  };
  const tGrate = textures.grating(), tPad = textures.padding(), tSteel = textures.steel(), tHaz = textures.hazard(), tCon = textures.console(), tDuct = textures.duct(), tPlate = textures.plate(), tPhos = textures.phosphor();

  // Corridor 2.2 wide × 2.6 high × 8 long; player stands at z=0 looking down -Z; the hatch is at the far end.
  box(2.3, 0.05, 9, mat(tGrate, [2, 8]), 0, -1.3, -3);
  box(2.3, 0.05, 9, mat(tDuct, [2, 8]), 0, 1.3, -3);
  box(0.05, 2.6, 9, mat(tPad, [8, 2.5]), -1.15, 0, -3);
  box(0.05, 2.6, 9, mat(tPad, [8, 2.5]), 1.15, 0, -3);
  box(2.3, 2.6, 0.05, mat(tSteel, [2, 2.5], 0x6f7a75), 0, 0, 1.5); // behind the player
  // Ribs every 1.5 m
  for (let z = -1; z > -8; z -= 1.5) { box(2.3, 0.12, 0.12, mat(tSteel, [2, 0.2], 0x55605b), 0, 1.2, z); box(0.12, 2.6, 0.12, mat(tSteel, [0.2, 2.5], 0x55605b), -1.1, 0, z); box(0.12, 2.6, 0.12, mat(tSteel, [0.2, 2.5], 0x55605b), 1.1, 0, z); }
  // The hatch: a heavy door with a wheel and hazard frame at the far end.
  box(2.3, 2.6, 0.08, mat(tSteel, [2, 2.5], 0x5a6560), 0, 0, -7.5);
  box(1.4, 2.0, 0.1, mat(tSteel, [1.5, 2], 0x7f8a85), 0, -0.2, -7.45);
  box(1.5, 0.12, 0.12, mat(tHaz, [3, 0.3]), 0, 0.85, -7.4);
  box(1.5, 0.12, 0.12, mat(tHaz, [3, 0.3]), 0, -1.25, -7.4);
  const wheelGeo = new THREE.TorusGeometry(0.25, 0.035, 6, 10); geos.push(wheelGeo);
  const wheel = new THREE.Mesh(wheelGeo, mat(tSteel, [1, 1], 0x9aa39e)); wheel.position.set(0, -0.2, -7.38); g.add(wheel);
  // Kiosk on the right bulkhead, angled toward the player: a housing with the CRT face.
  const kiosk = new THREE.Group(); kiosk.position.set(0.55, 0.08, -0.95); kiosk.rotation.y = -0.38; g.add(kiosk);
  const housingGeo = new THREE.BoxGeometry(0.8, 0.9, 0.32); geos.push(housingGeo);
  const housing = new THREE.Mesh(housingGeo, mat(tSteel, [1.5, 1.5], 0x7d8883)); kiosk.add(housing);
  const faceGeo = new THREE.BoxGeometry(0.8, 0.25, 0.34); geos.push(faceGeo);
  const keys = new THREE.Mesh(faceGeo, mat(tCon, [2, 0.4])); keys.position.set(0, -0.58, 0); kiosk.add(keys);
  const plateGeo = new THREE.BoxGeometry(0.5, 0.08, 0.02); geos.push(plateGeo);
  const plate = new THREE.Mesh(plateGeo, mat(tPlate, [1, 0.5], 0x9a9384)); plate.position.set(0, 0.5, 0.17); kiosk.add(plate);
  // Chamfer around the kiosk glass, same idea as the reactor tube.
  const chGeo = new THREE.BoxGeometry(0.7, 0.03, 0.05); geos.push(chGeo);
  for (const y of [0.29, -0.19]) { const c = new THREE.Mesh(chGeo, mat(tSteel, [2, 1], 0x8b958f)); c.position.set(0, y, 0.15); kiosk.add(c); }
  const chGeoV = new THREE.BoxGeometry(0.03, 0.51, 0.05); geos.push(chGeoV);
  for (const x of [-0.335, 0.335]) { const c = new THREE.Mesh(chGeoV, mat(tSteel, [1, 2], 0x8b958f)); c.position.set(x, 0.05, 0.15); kiosk.add(c); }
  const screenGeo = new THREE.PlaneGeometry(0.64, 0.48); geos.push(screenGeo);
  const screenMat = psxEmissive(tPhos, shared, 0x88ff99); mats.push(screenMat);
  const screen = new THREE.Mesh(screenGeo, screenMat); screen.position.set(0, 0.05, 0.165); kiosk.add(screen);
  const crt = new THREE.Object3D(); kiosk.add(crt); crt.position.copy(screen.position);
  kiosk.updateMatrixWorld(true);
  shared.uScreenPos.value.setFromMatrixPosition(crt.matrixWorld); // the kiosk lights the corridor
  shared.uScreenColor.value.set(0x88ff99).multiplyScalar(0.5);
  // Card slot lamp
  const lampGeo = new THREE.BoxGeometry(0.05, 0.05, 0.02); geos.push(lampGeo);
  const lampMat = psxEmissive(tPlate, shared, 0xff3322); mats.push(lampMat);
  const lamp = new THREE.Mesh(lampGeo, lampMat); lamp.position.set(0.3, -0.58, 0.18); kiosk.add(lamp);
  // Caged work light on the left wall (emissive block)
  const beaconGeo = new THREE.BoxGeometry(0.25, 0.08, 0.12); geos.push(beaconGeo);
  const beaconMat = psxEmissive(tPlate, shared, 0xffc070); mats.push(beaconMat);
  const beacon = new THREE.Mesh(beaconGeo, beaconMat); beacon.position.set(-1.08, 0.9, -2.6); g.add(beacon);
  // Clutter: crates
  box(0.5, 0.5, 0.5, mat(tSteel, [1, 1], 0x6a6358), -0.8, -1.0, -4.2);
  box(0.5, 0.3, 0.5, mat(tHaz, [1, 0.6]), -0.8, -0.6, -4.2);

  const intercom = new THREE.Object3D(); g.add(intercom); intercom.position.set(0, 5, 0); // unused here
  const lever = new THREE.Object3D(); g.add(lever);
  // Corridor notice pane, hung from the ceiling ribs across from the kiosk.
  const paneGlow = psxEmissive(tPhos, shared, 0x183d4a); mats.push(paneGlow);
  const noticePane = hangPlate(g, geos, mat, tSteel, { x: -0.02, y: 0.17, z: 0.42, yaw: 0.08, ceilingY: 1.28, w: 0.3, h: 0.23, glow: paneGlow });
  // Two things to touch while you wait for the rest of the crew: the deck light, and a call
  // button that has never once been answered.
  const fixtures = new Fixtures();
  const motion: RoomMotion = { fan: [], needles: [], flicker: [] };
  const pickMat = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: false }); mats.push(pickMat);
  {
    const plateGeo = new THREE.BoxGeometry(0.12, 0.16, 0.05); geos.push(plateGeo);
    const backing = new THREE.Mesh(plateGeo, mat(tSteel, [1, 1], 0x6c7570)); backing.position.set(0.86, 0.05, -1.9); g.add(backing);
    const capGeo = new THREE.CylinderGeometry(0.026, 0.028, 0.03, 10); geos.push(capGeo);
    const cap = new THREE.Group(); cap.position.set(0.83, 0.05, -1.9); cap.rotation.z = Math.PI / 2; g.add(cap);
    const capMesh = new THREE.Mesh(capGeo, mat(tPlate, [1, 1], 0x9a2a20)); cap.add(capMesh);
    const hitGeo = new THREE.BoxGeometry(0.1, 0.1, 0.1); geos.push(hitGeo);
    const hit = new THREE.Mesh(hitGeo, pickMat); cap.add(hit);
    fixtures.add({ id: "gangway.call", label: "CALL DECK OFFICER", kind: "button", pivot: cap, hit, travel: -0.014 });
  }
  return {
    group: g, anchors: { crt, intercom, plate: noticePane, lever, beacon, lamp }, fixtures, motion,
    dispose() { for (const m of mats) m.dispose(); for (const geo of geos) geo.dispose(); for (const t of [tGrate, tPad, tSteel, tHaz, tCon, tDuct, tPlate, tPhos]) t.dispose(); },
  };
}
